"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type AuthContextValue = {
  loggedIn: boolean;
  loading: boolean;
  avatarUrl: string | null;
  /** True for a logged-in account that still has no password (OAuth-only, not yet converted) — see
   * ApplicationUser.IsProspective on the API side. Meaningless while loggedIn is false. */
  isProspective: boolean;
  /** True when the JWT's role claim includes "Admin" — drives the AuthMenu's Admin link only. Not a
   * security boundary: /admin's own layout guard and every admin API call re-check the real role
   * server-side. See docs/architecture/admin-module.md. */
  isAdmin: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isProspective, setIsProspective] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/session");
      const data = await response.json();
      setLoggedIn(!!data.loggedIn);
      setAvatarUrl(data.avatarUrl ?? null);
      setIsProspective(!!data.isProspective);
      setIsAdmin(!!data.isAdmin);
    } catch {
      setLoggedIn(false);
      setAvatarUrl(null);
      setIsProspective(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // See cart-context.tsx for why the mount-time fetch is its own effect-local closure rather than
  // an effect depending on the memoized `refresh` callback (react-hooks/set-state-in-effect).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/auth/session");
        const data = await response.json();
        if (!cancelled) {
          setLoggedIn(!!data.loggedIn);
          setAvatarUrl(data.avatarUrl ?? null);
          setIsProspective(!!data.isProspective);
          setIsAdmin(!!data.isAdmin);
        }
      } catch {
        if (!cancelled) {
          setLoggedIn(false);
          setAvatarUrl(null);
          setIsProspective(false);
          setIsAdmin(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setLoggedIn(false);
    setAvatarUrl(null);
    setIsProspective(false);
    setIsAdmin(false);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ loggedIn, loading, avatarUrl, isProspective, isAdmin, refresh, logout }),
    [loggedIn, loading, avatarUrl, isProspective, isAdmin, refresh, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
